import { useState, useEffect, useCallback } from 'react';

/**
 * 갱신율 · 이탈 · 좌석 가동률 — 매출 기준(입금/이용)과 무관한 지표라 별도 훅으로 뒀다.
 * 매출 화면에서 기준을 토글해도 이쪽은 다시 부르지 않는다.
 */
export interface RenewalPoint {
  month: string;
  expired: number;
  renewed: number;
  churned: number;
  pending: number;
  /** 체험권(무료·7일 미만) 만료 — 갱신율 모집단에서 뺀 수 */
  trial_expired: number;
  renewal_rate: number | null;
  /** 대기 0 = 판정 끝난 달. false 면 잠정치라 '집계 중' 으로 보여야 한다 */
  settled: boolean;
}

export interface ChurnedMember {
  user_id: string;
  name: string;
  phone: string;
  end_date: string | null;
  last_price: number;
  last_voucher: string;
}

export interface RetentionMetrics {
  month: string;
  as_of: string;
  capacity: {
    seats: number;
    active_members: number;
    occupancy_rate: number | null;
    empty_seats: number;
  };
  renewal: RenewalPoint;
  renewal_trend: RenewalPoint[];
  avg_renewal_rate: number | null;
  churned_members: ChurnedMember[];
  upcoming: {
    month: string;
    count: number;
    amount: number;
    forecast_amount: number | null;
  };
}

export const useRetentionMetrics = (month: string) => {
  const [data, setData] = useState<RetentionMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { apiClient } = await import('../../../infrastructure/api');
      const res = await apiClient.get(
        `/v1/owner/payment-management/revenue/retention?month=${month}&trend_months=6`
      );
      setData(res.data);
    } catch (e: any) {
      console.error('갱신율 조회 실패:', e?.response?.status, e?.message);
      setError(e?.response?.data?.detail || '갱신 지표를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
};
